import { useToast } from "@/composables/toast";
import { useAuthStore, useCampagneStore, useCommandeStore } from "@/context";
import { useNavigate } from "react-router-dom";
import { UserTypes } from "../users";
import { UserModel } from "../../app/entities";
import { useState } from "react";
import { APIURI } from "@/constants/api.uris";
import useApi, {AppApiResponse} from "@/services/use.api";
import { getCurrentUserDashboard } from "./utils";
import { useNotifications } from "@/hooks/useNotifications";

const { usePostApi, useGetApi, usePutApi, useDeleteApi } = useApi();

export const useAuth = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    isAuthenticated,
    profilePicture,
    setProfilePicture,
    setIsAuthenticated,
    setCurrentUser,
    resetMe,
  } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [savingPicture, setSavingPicture] = useState(false);
  const [updating, setUpdating] = useState(false);

  const commandeStore = useCommandeStore();
  const campagneStore = useCampagneStore();

  const { subscribeToAllTopics, fetchNotifications } = useNotifications();

  const login = async (params: { login: string; password: string }): Promise<UserModel> => {
    setLoading(true);
    const res= await usePostApi<any>(APIURI.AuthLogin, params);
    const { success, message, data } = res ?? {};

    if (!success) {
      useToast(message || "Identifiants incorrects", {
        variant: "error",
      });
      setLoading(false);
    }

    setCurrentUser(data);
    setIsAuthenticated(true);
    useToast("Authentification reussie", { variant: "success" });
    subscribeToAllTopics();
    fetchNotifications();
    navigate(getCurrentUserDashboard(data as UserModel));
    setLoading(false);
    return data;
  };

  const logout = async (): Promise<void> => {
    const res = await useDeleteApi<any>(`/auth/session`);
    const { success } = res ?? {};
    if (!success) {
      useToast("Erreur lors de la déconnexion", { variant: "error" });
      return;
    }
    resetMe(() => {
      commandeStore.clearStore();
      campagneStore.clearStore();
      localStorage.clear();
    });
    navigate("/connexion");
  };

  const requestPasswordRecoveryCode = async (
    email: string
  ): Promise<boolean> => {
    setLoading(true);
    const res = await useGetApi(`/public/authentification/${email}`);
    const { success, message } = res ?? {};
    setLoading(false);
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return false;
    }
    useToast("Code de récupération envoyé", {
      variant: "success",
    });
    return true;
  };

  const verifyRecoveryCode = async (params: {
    email: string;
    code: string;
    isProfessional?: boolean;
  }): Promise<string | null> => {
    setLoading(true);
    const res = await useGetApi<any>(
      `/public/authentification/${params.email}/${params.code}/${
        params.isProfessional || false
      }`
    );
    const { success, message, data } = res ?? {};
    setLoading(false);
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return null;
    }
    return data as string;
  };

  const changePassword = async (params: {
    login: string;
    password: string;
    isProfessional?: boolean;
  }): Promise<boolean> => {
    setLoading(true);
    const res = await usePutApi<any>(`/public/authentification`, params);
    const { success, message, data } = res ?? {};

    setLoading(false);
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return false;
    }

    useToast(message ?? "Mot de passe modifié avec succès.", {
      variant: "success",
    });
    return true;
  };

  const isAdmin = (): boolean => {
    return currentUser?.type === UserTypes.ADMINISTRATEUR;
  };

  const currentUserHasRole = (role: string): boolean => {
    return currentUser?.type === role;
  };

  const currentUserAuthorized = (roles: string[]): boolean => {
    return roles.includes(currentUser?.type ?? "");
  };

  const userHasRole = (user: UserModel, role: string): boolean => {
    return user?.type === role;
  };

  const userAuthorized = (roles: string[], user?: UserModel): boolean => {
    return roles.includes(user?.type ?? "");
  };

  const fetchProfilePicture = async () => {
    setLoading(true);
    const res = await useGetApi<any>(`/auth/session/profil`, {
      responseType: "blob",
      headers: {
        Accept: " application/octet-stream",
      },
    });
    const { data, success } = res ?? {};
    if (success && data) {
      try {
        if (typeof data === "string") {
          // Si c'est une chaîne, c'est probablement du base64
          if (data.startsWith("data:image/")) {
            // Déjà formaté comme data URL
            setProfilePicture(data);
          } else {
            // Base64 brut, ajouter le préfixe data URL
            setProfilePicture(`data:image/jpeg;base64,${data}`);
          }
        } else if (data instanceof Blob) {
          // Si c'est un Blob, utiliser FileReader
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            setProfilePicture(base64String);
          };
          reader.readAsDataURL(data);
        } else if (data instanceof ArrayBuffer) {
          // Si c'est un ArrayBuffer, convertir en Blob puis en base64
          const blob = new Blob([data], { type: "image/jpeg" });
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            setProfilePicture(base64String);
          };
          reader.readAsDataURL(blob);
        }
      } catch (error: any) {
        setProfilePicture("/assets/images/avatar.png");
      }
    } else {
      setProfilePicture("/assets/images/avatar.png");
    }
    setLoading(false);
  };

  const upateProfilePicture = async (params: FormData) => {
    setSavingPicture(true);
    const res = await usePostApi<any>(`/auth/session`, params, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    const { success, message, data } = res ?? {};
    setSavingPicture(false);
    fetchProfilePicture();
  };

  const updateProfileInfos = async (params: any) => {
    setUpdating(true);
    const res = await usePutApi<UserModel>(`/auth/session`, params);
    const { success, data } = res ?? {};
    if (success && data) {
      setCurrentUser(data);
      useToast("Profil mis à jour avec succès.", {
        variant: "success",
      });
    }
    setUpdating(false);
  };

  return {
    loading,
    isAuthenticated,
    savingPicture,
    updating,
    updateProfileInfos,
    fetchProfilePicture,
    login,
    logout,
    isAdmin,
    setSavingPicture,
    userHasRole,
    changePassword,
    userAuthorized,
    currentUserHasRole,
    verifyRecoveryCode,
    currentUserAuthorized,
    requestPasswordRecoveryCode,
    upateProfilePicture,
  };
};
